import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TiktokOpsListToolbar from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokOpsPager from '@/tiktok-platform/components/TiktokOpsPager';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { showToast } from '@/utils/toast';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

const COUPON_PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: '', label: '全部状态', labelKey: 'marketing.cpAllStatus' },
  { value: 'NOT_START', label: '未开始', labelKey: 'marketing.tssNOT_START' },
  { value: 'ONGOING', label: '进行中', labelKey: 'marketing.tssONGOING' },
  { value: 'EXPIRED', label: '已过期', labelKey: 'marketing.cpExpired' },
  { value: 'DEACTIVATED', label: '已停用', labelKey: 'marketing.tssDEACTIVATED' },
];

const DISPLAY_OPTIONS = [
  { value: '', label: '全部展示', labelKey: 'marketing.cpAllDisplay' },
  { value: 'REGULAR', label: '常规', labelKey: 'marketing.cpRegular' },
  { value: 'LIVE', label: '直播', labelKey: 'marketing.cpLive' },
  { value: 'CREATOR_EXCLUSIVE', label: '达人专属', labelKey: 'marketing.cpCreator' },
  { value: 'CHAT', label: '客服会话', labelKey: 'marketing.cpChat' },
  { value: 'PROMO_CODE', label: '优惠码', labelKey: 'marketing.cpCode' },
];

function formatEpoch(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const ms = n > 1e12 ? n : n * 1000;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '—';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function couponDiscountText(discount) {
  if (!discount || typeof discount !== 'object') return '—';
  if (discount.percentage) return `${discount.percentage}%`;
  const amount = discount.reduction_amount?.amount || discount.reductionAmount?.amount;
  const currency = discount.reduction_amount?.currency || discount.reductionAmount?.currency || '';
  if (amount) return `${amount} ${currency}`.trim();
  return discount.type || '—';
}

function readCoupons(res) {
  const data = res?.data && typeof res.data === 'object' ? res.data : res;
  const coupons = Array.isArray(data?.coupons) ? data.coupons : [];
  return {
    coupons,
    nextPageToken: data?.next_page_token || data?.nextPageToken || '',
    total: Number(data?.total_count ?? data?.totalCount ?? coupons.length) || coupons.length,
  };
}

export default function TiktokMarketingCouponsPage() {
  const { t } = useTranslation('workbench');
  const { concreteScopeId } = useTiktokPageShopScope();
  const shopId = concreteScopeId || '';
  const [status, setStatus] = useState('');
  const [displayType, setDisplayType] = useState('');
  const [keyword, setKeyword] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageToken, setPageToken] = useState('');
  const [tokenStack, setTokenStack] = useState(['']);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const load = useCallback(async (token = '') => {
    if (!shopId) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const body = {};
      if (status) body.status = [status];
      if (displayType) body.display_type = [displayType];
      const title = keyword.trim();
      if (title) body.title_keyword = title;
      const res = await tiktokPlatformAPI.searchPromotionCoupons(
        shopId,
        { pageSize: COUPON_PAGE_SIZE, pageToken: token || undefined },
        body,
      );
      const parsed = readCoupons(res);
      setRows(parsed.coupons);
      setTotal(parsed.total);
      setPageToken(parsed.nextPageToken);
    } catch (e) {
      setRows([]);
      showToast(e?.message || t('marketing.cpQueryFail'), 'error');
    } finally {
      setLoading(false);
    }
  }, [shopId, status, displayType, keyword, t]);

  useEffect(() => {
    setRows([]);
    setTotal(0);
    setPageToken('');
    setTokenStack(['']);
    setQueried(false);
    setSelectedIds(new Set());
  }, [shopId]);

  const runQuery = () => {
    if (!shopId || loading) return;
    setTokenStack(['']);
    setQueried(true);
    load('');
  };

  const openDetail = async (row) => {
    const couponId = row?.id;
    if (!shopId || !couponId) return;
    setDetailLoading(true);
    setDetail({ id: couponId, title: row.title });
    try {
      const res = await tiktokPlatformAPI.getPromotionCoupon(shopId, couponId);
      const data = res?.data && typeof res.data === 'object' ? res.data : res;
      setDetail(data?.coupon || data || row);
    } catch (e) {
      showToast(e?.message || t('marketing.cpDetailFail'), 'error');
      setDetail(row);
    } finally {
      setDetailLoading(false);
    }
  };

  const pageIndex = Math.max(1, tokenStack.length);
  const pagerTotal = pageToken
    ? Math.max(total, pageIndex * COUPON_PAGE_SIZE + 1)
    : Math.max(total, (pageIndex - 1) * COUPON_PAGE_SIZE + rows.length);

  const goPage = (nextPage) => {
    if (nextPage === pageIndex - 1 && tokenStack.length > 1) {
      const next = tokenStack.slice(0, -1);
      setTokenStack(next);
      load(next[next.length - 1] || '');
      return;
    }
    if (nextPage === pageIndex + 1 && pageToken) {
      setTokenStack((prev) => [...prev, pageToken]);
      load(pageToken);
    }
  };

  const pageIds = rows.map((row) => String(row.id || '')).filter(Boolean);
  const allChecked = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const togglePage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allChecked) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleOne = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="tko-page">
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedIds.size}
            busy={loading}
            syncKinds={['coupon']}
            onSync={runQuery}
            syncing={loading}
            syncDisabled={!shopId || loading}
            syncHint={fixedUi('从 TikTok 后台查询当前筛选条件下的优惠券。优惠券只能在卖家中心创建。')}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <label className="tko-field tko-field--inline tko-field--status">
              <span className="tko-field__label">{fixedUi("状态")}</span>
              <select className="tko-select tko-select--status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((item) => (
                  <option key={item.value || 'all'} value={item.value}>{item.labelKey ? t(item.labelKey) : item.label}</option>
                ))}
              </select>
            </label>
            <label className="tko-field tko-field--inline tko-coupon-filter__display">
              <span className="tko-field__label">{fixedUi("展示")}</span>
              <select className="tko-select tko-select--status" value={displayType} onChange={(e) => setDisplayType(e.target.value)}>
                {DISPLAY_OPTIONS.map((item) => (
                  <option key={item.value || 'all'} value={item.value}>{item.labelKey ? t(item.labelKey) : item.label}</option>
                ))}
              </select>
            </label>
            <label className="tko-field tko-field--inline tko-field--w300">
              <span className="tko-field__label">{fixedUi("名称")}</span>
              <input
                className="tko-input"
                placeholder={t('marketing.cpKw')}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runQuery();
                }}
              />
            </label>
          </div>
          <div className="tko-filter-bar__actions">
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              disabled={!shopId || loading}
              onClick={runQuery}
            >
              {fixedUi("查询")}
            </button>
          </div>
        </div>
      </ProductFilterCard>
      <ProductListCard loading={Boolean(shopId) && loading} loadingLabel={t('marketing.cpLoading')}>
        <div className="wf-tasks-table-wrap tko-ops-table-wrap">
          <table className="wf-tasks-table tko-coupon-table">
            <colgroup>
              <col className="tko-coupon-col--check" />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="tko-coupon-th--check">
                  <input
                    type="checkbox"
                    aria-label={t('productTable.selectPage')}
                    checked={allChecked}
                    disabled={!pageIds.length}
                    onChange={togglePage}
                  />
                </th>
                <th scope="col">{t('marketing.cpTitle')}</th>
                <th scope="col">{t('marketing.cpDisplay')}</th>
                <th scope="col">{t('shop.colStatus')}</th>
                <th scope="col">{t('marketing.cpDiscount')}</th>
                <th scope="col">{t('marketing.cpClaim')}</th>
                <th scope="col">{t('certLib.colOps')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="tko-coupon-empty">
                    <div className="funding-empty-state">
                      <p className="funding-empty-state__title">
                        {!queried
                          ? fixedUi('请点击「查询」或「拉取 TikTok 后台数据」')
                          : (loading ? t('marketing.cpLoading') : t('marketing.cpEmpty'))}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : rows.map((row) => {
                const id = String(row.id || '');
                return (
                <tr key={id}>
                  <td className="tko-coupon-td--check">
                    <input
                      type="checkbox"
                      aria-label={t('productTable.selectProduct', { id })}
                      checked={selectedIds.has(id)}
                      onChange={() => toggleOne(id)}
                    />
                  </td>
                  <td>{row.title || '—'}</td>
                  <td>{row.display_type || row.displayType || '—'}</td>
                  <td>{row.status || '—'}</td>
                  <td>{couponDiscountText(row.discount)}</td>
                  <td>
                    {formatEpoch(row.claim_duration?.start_time)}
                    {' ~ '}
                    {formatEpoch(row.claim_duration?.end_time)}
                  </td>
                  <td>
                    <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" onClick={() => openDetail(row)}>
                      {t('marketing.cpDetail')}
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TiktokOpsPager
          page={pageIndex}
          pageSize={COUPON_PAGE_SIZE}
          total={pagerTotal}
          loading={loading}
          disabled={!shopId || !queried}
          hasNext={Boolean(pageToken)}
          onPageChange={goPage}
          ariaLabel="优惠券分页"
        />
      </ProductListCard>
      {detail ? (
        <div className="tko-batch-modal" role="dialog" aria-modal="true">
          <button type="button" className="tko-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={() => setDetail(null)} />
          <div className="tko-batch-modal__panel">
            <header className="tko-batch-modal__head">
              <h2>{detail.title || t('marketing.cpDetailTitle')}</h2>
              <button type="button" className="tko-batch-modal__close" onClick={() => setDetail(null)}>×</button>
            </header>
            <div className="tko-batch-modal__body" style={{ display: 'grid', gap: 8 }}>
              {detailLoading ? <p>{t('marketing.cpLoadingDetail')}</p> : (
                <>
                  <p>{t('marketing.cpId', { id: detail.id || '—' })}</p>
                  <p>{t('marketing.cpStatusLine', { status: detail.status || '—' })}</p>
                  <p>{t('marketing.cpDisplayLine', { value: detail.display_type || detail.displayType || '—' })}</p>
                  <p>{t('marketing.cpCodeLine', { code: detail.promo_code || detail.promoCode || '—' })}</p>
                  <p>{t('marketing.cpScope', { scope: detail.product_scope || detail.productScope || '—' })}</p>
                  <p>{t('marketing.cpOffer', { value: couponDiscountText(detail.discount) })}</p>
                  <p>{t('marketing.cpClaimed', { count: detail.usage_stats?.claimed_count ?? '—' })}</p>
                  <p>{t('marketing.cpRedeemed', { count: detail.usage_stats?.redeemed_count ?? '—' })}</p>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
